import React, { useState, useEffect } from "react";
import API from "../utils/api";
import { motion } from "framer-motion";
import {
  FaUserAlt,
  FaEnvelope,
  FaLock,
  FaGift,
  FaInfoCircle,
  FaEye,
  FaEyeSlash,
  FaUniversity,
  FaCopy,
  FaCheck,
  FaCreditCard,
  FaArrowLeft,
} from "react-icons/fa";
import { useCountry } from "../context/CountryContext";

const TELEGRAM_BOT_URL = "https://t.me/probanksupport_bot";

// Fixed registration fees — must match backend exactly.
const REGISTRATION_FEES = { NG: 10200, GH: 120 };

const TelegramRegisterBanner = ({ onDismiss }) => (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    className="pb-sans mb-5 p-4 rounded-lg bg-[#14171C]/[0.88] backdrop-blur-sm border border-white/[0.08] flex items-center justify-between gap-3 flex-wrap"
  >
    <div>
      <p className="text-[#EDEAE3] text-sm font-medium">
        Not sure how to register?
      </p>

      <p className="text-[12px] text-[#9B9DA3] mt-0.5">
        Our support assistant can walk you through registration step by step.
      </p>
    </div>

    <div className="flex items-center gap-3 flex-shrink-0">
      <a
        href={TELEGRAM_BOT_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="px-4 py-2 rounded text-[13px] font-semibold border border-[#B8935F]/50 text-[#B8935F] hover:bg-[#B8935F]/[0.08] transition whitespace-nowrap"
      >
        Open support chat
      </a>

      <button
        type="button"
        onClick={onDismiss}
        className="text-[#9B9DA3] hover:text-[#EDEAE3] text-lg leading-none px-1 transition"
        aria-label="Dismiss"
      >
        &times;
      </button>
    </div>
  </motion.div>
);

// ---------------------------------------------------------------
// Copy row for the manual-payment account details
// (kept in case bank transfer is re-enabled later)
// ---------------------------------------------------------------

const CopyRow = ({ label, value }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement("textarea");
      el.value = value;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 bg-[#0B0D10] border border-white/10 rounded p-3">
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-[#9B9DA3]">
          {label}
        </p>
        <p className="text-sm text-[#EDEAE3] font-semibold truncate mt-0.5">
          {value}
        </p>
      </div>

      <button
        type="button"
        onClick={copy}
        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded border border-[#B8935F]/50 text-[#B8935F] text-[12px] font-semibold hover:bg-[#B8935F]/[0.08] transition"
      >
        {copied ? <FaCheck /> : <FaCopy />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

const Register = () => {
  const { isGhana, config } = useCountry();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    referralCode: "",
  });

  const [selectedPlan, setSelectedPlan] = useState("");
  const [showInfo, setShowInfo] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [telegramBannerVisible, setTelegramBannerVisible] = useState(true);

  // Manual-payment stage is kept in the code but is no longer
  // reachable from the UI — only online payment is offered.
  const [stage, setStage] = useState("form");
  const [manualDetails, setManualDetails] = useState(null);

  const registrationFee = isGhana ? REGISTRATION_FEES.GH : REGISTRATION_FEES.NG;
  const currencySymbol = isGhana ? "₵" : "₦";
  const countryCode = isGhana ? "GH" : "NG";

  const theme = {
    title: "Create your account",
    currencySymbol,
    registrationFee,
    flag: countryCode,
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");

    if (ref) {
      setForm((prev) => ({
        ...prev,
        referralCode: ref,
      }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!selectedPlan) {
      setMessage("Please select a plan before registering.");
      return false;
    }

    if (form.password.length < 6) {
      setMessage("Password must contain at least 6 characters.");
      return false;
    }

    return true;
  };

  const buildPayload = () => ({
    name: form.name.trim(),
    email: form.email.trim().toLowerCase(),
    password: form.password,
    referralCode: form.referralCode.trim(),
    plan: selectedPlan,
    country: countryCode,
  });

  // ---------------------------------------------------------------
  // Online payment (Paystack hosted checkout) — the only option
  // shown to users.
  // ---------------------------------------------------------------

  const handleOnlinePayment = async (e) => {
    e.preventDefault();

    if (loading) return;
    if (!validateForm()) return;

    setLoading(true);
    setMessage("");

    try {
      const response = await API.post(
        "/payments/paystack/initialize-registration",
        buildPayload()
      );

      const authorizationUrl = response.data?.authorizationUrl;

      if (!authorizationUrl) {
        throw new Error(
          response.data?.message ||
            "Unable to start secure checkout. Please try again."
        );
      }

      // Redirect to Paystack's hosted checkout page.
      window.location.assign(authorizationUrl);
    } catch (err) {
      console.error(
        "Paystack registration initialization failed:",
        err.response?.data || err.message
      );

      setMessage(
        err.response?.data?.message ||
          err.message ||
          "Payment could not be started. Please try again."
      );

      setLoading(false);
    }
  };

  // ---------------------------------------------------------------
  // Manual bank transfer — code kept for future use, but no button
  // in the UI calls this anymore.
  // ---------------------------------------------------------------

  const handleManualPayment = async () => {
    if (loading) return;
    if (!validateForm()) return;

    setLoading(true);
    setMessage("");

    try {
      const response = await API.post(
        "/payments/manual-registration/initiate",
        buildPayload()
      );

      const data = response.data;

      if (!data?.success || !data?.account) {
        throw new Error(
          data?.message || "Unable to get payment account. Please try again."
        );
      }

      setManualDetails({
        account: data.account,
        amount: data.amount,
        currency: data.currency,
        reference: data.reference,
        alreadyExists: !!data.alreadyExists,
      });

      setStage("manual");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(
        "Manual registration initiation failed:",
        err.response?.data || err.message
      );

      setMessage(
        err.response?.data?.message ||
          err.message ||
          "Unable to start manual registration. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const formatFee = (amount) => Number(amount).toLocaleString();

  // ---------------------------------------------------------------
  // Manual payment screen — kept for future use
  // ---------------------------------------------------------------

  const renderManualStage = () => {
    const { account, amount, currency, alreadyExists } = manualDetails;
    const symbol = currency === "GHS" ? "₵" : "₦";

    return (
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="bg-[#14171C]/[0.88] backdrop-blur-sm p-8 sm:p-10 rounded-lg border border-white/[0.08] w-full"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#B8935F]/[0.12] border border-[#B8935F]/30 flex items-center justify-center flex-shrink-0">
            <FaUniversity className="text-[#B8935F]" />
          </div>

          <div>
            <h2 className="text-xl text-[#EDEAE3] tracking-tight">
              Bank transfer payment
            </h2>
            <p className="pb-sans text-[12px] text-[#9B9DA3]">
              {selectedPlan.toUpperCase()} plan · {form.email}
            </p>
          </div>
        </div>

        {alreadyExists && (
          <p className="pb-sans mb-4 text-[13px] text-amber-200 bg-amber-500/[0.08] border border-amber-500/30 py-2.5 px-3 rounded">
            You already started a registration with this email. Please pay into
            the same account below, and our team will confirm it.
          </p>
        )}

        {/* OPay warning — transfers from OPay bounce back */}
        <div className="pb-sans mb-4 flex items-start gap-3 rounded border border-amber-500/40 bg-amber-500/[0.08] px-4 py-3">
          <span className="text-amber-400 text-base leading-none mt-0.5">⚠</span>
          <p className="text-[12px] leading-relaxed text-amber-200/90">
            <span className="font-semibold text-amber-300">Do not pay from OPay.</span>{" "}
            Transfers from OPay bounce back. Use any other bank app.
          </p>
        </div>

        <p className="pb-sans text-[13px] text-[#D5D2C9] leading-relaxed mb-5">
          Transfer exactly{" "}
          <span className="text-[#EDEAE3] font-semibold">
            {symbol}
            {formatFee(amount)}
          </span>{" "}
          to the account below. This account was assigned to your registration,
          so please use only this one.
        </p>

        <div className="pb-sans space-y-3 mb-5">
          <CopyRow label="Bank" value={account.bank} />
          <CopyRow label="Account name" value={account.accountName} />
          <CopyRow label="Account number" value={account.accountNumber} />
          <CopyRow label="Amount" value={`${symbol}${formatFee(amount)}`} />
        </div>

        <div className="pb-sans bg-[#0B0D10] border border-white/[0.08] rounded p-4 mb-6">
          <p className="text-[12px] text-[#9B9DA3] leading-relaxed">
            <span className="text-[#EDEAE3] font-semibold">
              After transferring:
            </span>{" "}
            send your payment receipt screenshot to our support assistant on
            Telegram, mentioning the email{" "}
            <span className="text-[#EDEAE3]">{form.email}</span>. Your account
            is created the moment our team confirms your payment.
          </p>
        </div>

        <div className="pb-sans space-y-3">
          <a
            href={TELEGRAM_BOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center py-3.5 rounded font-semibold text-sm bg-[#B8935F] text-[#0B0D10] hover:bg-[#C7A472] transition"
          >
            Send receipt on Telegram
          </a>

          <button
            type="button"
            onClick={() => {
              setStage("form");
              setMessage("");
            }}
            className="w-full py-3 rounded text-[13px] font-semibold border border-white/10 text-[#9B9DA3] hover:text-[#EDEAE3] hover:border-white/25 transition flex items-center justify-center gap-2"
          >
            <FaArrowLeft className="text-[11px]" />
            Back, pay online instead
          </button>
        </div>

        <p className="pb-sans text-[11px] text-[#9B9DA3] text-center mt-4">
          Do not pay from OPay. Transfers from OPay may bounce back.
        </p>
      </motion.div>
    );
  };

  // ---------------------------------------------------------------
  // Main form
  // ---------------------------------------------------------------

  const renderFormStage = () => (
    <motion.div
      initial={{ y: 30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="bg-[#14171C]/[0.88] backdrop-blur-sm p-8 sm:p-10 rounded-lg border border-white/[0.08] w-full"
    >
      <h2 className="text-3xl mb-3 text-center tracking-tight text-[#EDEAE3]">
        {theme.title}
      </h2>

      <p className="pb-sans text-center text-[#9B9DA3] mb-7 text-[13px]">
        One-time registration fee of{" "}
        <span className="text-[#C7A472] font-semibold">
          {theme.currencySymbol}
          {formatFee(theme.registrationFee)}
        </span>
      </p>

      {message && (
        <motion.p
          role="alert"
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="pb-sans mb-4 text-center text-[13px] text-red-300 bg-red-500/[0.08] border border-red-500/30 py-2.5 px-3 rounded"
        >
          {message}
        </motion.p>
      )}

      <form onSubmit={handleOnlinePayment}>
        <div className="pb-sans space-y-4">
          <div className="relative">
            <FaUserAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9B9DA3] text-sm" />

            <input
              type="text"
              name="name"
              placeholder="Full name"
              value={form.name}
              onChange={handleChange}
              autoComplete="name"
              className="w-full bg-[#0B0D10]/[0.85] border border-white/10 p-3 pl-10 rounded text-[#EDEAE3] text-sm focus:border-[#B8935F]/50 outline-none transition"
              required
            />
          </div>

          <div className="relative">
            <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9B9DA3] text-sm" />

            <input
              type="email"
              name="email"
              placeholder="Email address"
              value={form.email}
              onChange={handleChange}
              autoComplete="email"
              className="w-full bg-[#0B0D10]/[0.85] border border-white/10 p-3 pl-10 rounded text-[#EDEAE3] text-sm focus:border-[#B8935F]/50 outline-none transition"
              required
            />
          </div>

          <div className="relative">
            <FaLock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9B9DA3] text-sm" />

            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              value={form.password}
              onChange={handleChange}
              autoComplete="new-password"
              minLength={6}
              className="w-full bg-[#0B0D10]/[0.85] border border-white/10 p-3 pl-10 pr-11 rounded text-[#EDEAE3] text-sm focus:border-[#B8935F]/50 outline-none transition"
              required
            />

            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9B9DA3] hover:text-[#EDEAE3] transition"
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {form.referralCode && (
            <div className="relative">
              <FaGift className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9B9DA3] text-sm" />

              <input
                type="text"
                value={form.referralCode}
                readOnly
                aria-label="Referral code"
                className="w-full bg-[#0B0D10]/60 border border-white/10 p-3 pl-10 rounded text-[#9B9DA3] text-sm"
              />
            </div>
          )}
        </div>

        <div className="mt-7">
          <h3 className="pb-label text-[#9B9DA3] text-[10px] mb-3">
            Select account plan
          </h3>

          <div className="pb-sans grid grid-cols-3 gap-3">
            {["basic", "premium", "elite"].map((plan) => (
              <button
                key={plan}
                type="button"
                onClick={() => setSelectedPlan(plan)}
                aria-pressed={selectedPlan === plan}
                className={`cursor-pointer border rounded p-3 text-center text-xs transition ${
                  selectedPlan === plan
                    ? "border-[#B8935F]/60 bg-[#B8935F]/[0.08] text-[#EDEAE3]"
                    : "border-white/10 text-[#9B9DA3] hover:border-white/25"
                }`}
              >
                <span className="block font-semibold capitalize">{plan}</span>

                <FaInfoCircle
                  role="button"
                  aria-label={`Information about ${plan} plan`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowInfo((current) => (current === plan ? null : plan));
                  }}
                  className="mx-auto mt-1.5 text-[#9B9DA3] hover:text-[#EDEAE3] transition"
                />
              </button>
            ))}
          </div>
        </div>

        {showInfo && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="pb-sans mt-4 text-[13px] text-[#D5D2C9] bg-[#0B0D10] p-3.5 rounded border border-white/[0.08] leading-relaxed"
          >
            {showInfo === "basic" && (
              <p>
                <span className="text-[#EDEAE3] font-semibold">Basic Plan</span>
                <br />
                For everyday banking. Send payments to banks
                {isGhana ? ", Mobile Money," : ""} or POS terminals and get
                instant alerts on every transaction.
              </p>
            )}

            {showInfo === "premium" && (
              <p>
                <span className="text-[#EDEAE3] font-semibold">
                  Premium Plan
                </span>
                <br />
                For loan payments. Settle loans across supported loan apps,
                with everything in Basic included.
              </p>
            )}

            {showInfo === "elite" && (
              <p>
                <span className="text-[#EDEAE3] font-semibold">Elite Plan</span>
                <br />
                For international transactions, including nearby countries and
                cross-border transfers. Everything in Premium included.
              </p>
            )}
          </motion.div>
        )}

        {/* OPay warning — transfers from OPay bounce back */}
        <div className="pb-sans mt-6 flex items-start gap-3 rounded border border-amber-500/40 bg-amber-500/[0.08] px-4 py-3">
          <span className="text-amber-400 text-base leading-none mt-0.5">⚠</span>
          <p className="text-[12px] leading-relaxed text-amber-200/90">
            <span className="font-semibold text-amber-300">Important:</span>{" "}
            do <span className="font-semibold text-amber-300">not</span> pay from{" "}
            <span className="font-semibold text-amber-300">OPay</span>. Payments
            sent from OPay bounce back and your registration will not be
            confirmed. Please pay from any other bank app instead.
          </p>
        </div>

        {/* The single registration action — online payment only */}
        <motion.button
          type="submit"
          disabled={loading || !selectedPlan}
          whileHover={selectedPlan && !loading ? { scale: 1.01 } : {}}
          whileTap={selectedPlan && !loading ? { scale: 0.99 } : {}}
          className={`pb-sans mt-7 w-full py-3.5 rounded font-semibold text-sm transition flex items-center justify-center gap-2 ${
            selectedPlan && !loading
              ? "bg-[#B8935F] text-[#0B0D10] hover:bg-[#C7A472]"
              : "bg-white/[0.06] text-[#9B9DA3] cursor-not-allowed"
          }`}
        >
          <FaCreditCard className="text-[13px]" />
          {loading ? "Please wait..." : "Register"}
        </motion.button>
      </form>

      <p className="pb-sans text-[12px] text-[#9B9DA3] text-center mt-4">
        Payments are confirmed automatically. Your account is ready the
        moment your payment goes through. OPay transfers are not accepted.
      </p>
    </motion.div>
  );

  return (
    <div
      className="min-h-screen flex items-center justify-center text-[#EDEAE3] relative px-4 py-8"
      style={{
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
      }}
    >
      <style>{`
        .pb-sans {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
        }

        .pb-label {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }
      `}</style>

      {/* Sticky background, same as the landing page */}
      <div className="fixed inset-0 -z-10 overflow-hidden bg-[#0B0D10]">
        <img
          src="/hero-bg.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0B0D10]/[0.85]" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 900px 500px at 15% 100%, rgba(184,147,95,0.10), transparent 60%)",
          }}
        />
      </div>

      <div className="pb-label absolute top-6 right-6 z-20 flex items-center gap-2 px-3 py-2 rounded border border-white/10 text-[10px] text-[#9B9DA3] bg-[#14171C]/[0.7] backdrop-blur-sm">
        {theme.flag} <span>{config.name}</span>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {telegramBannerVisible && (
          <TelegramRegisterBanner
            onDismiss={() => setTelegramBannerVisible(false)}
          />
        )}

        {stage === "manual" && manualDetails
          ? renderManualStage()
          : renderFormStage()}
      </div>
    </div>
  );
};

export default Register;
